"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createChatClient = createChatClient;
var _constants = require("../utils/constants");
// Builds the REST client used alongside the socket adapter. Deliberately
// takes an INJECTED request object (each app's own Gateway.js get/post/put/del
// functions) rather than importing axios/Gateway.js/UrlHelper.js itself, so:
//  - lobees-web's Bearer-token request interceptor and lobees-admin's
//    cookie-based + auth-redirect response interceptor both keep working
//    completely unmodified.
//  - the package never needs to know whether a failed call returns a bare
//    string (web) or an error payload object (admin) — it only relies on
//    `response.status` (confirmed common to both apps' success responses) and
//    delegates the failure-string shape to the caller-supplied normalizeError.
function createChatClient() {
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    request = _ref.request,
    _ref$endpoints = _ref.endpoints,
    endpoints = _ref$endpoints === void 0 ? {} : _ref$endpoints,
    socketUrl = _ref.socketUrl,
    normalizeError = _ref.normalizeError;
  if (!request || !request.get || !request.post || !request.put) {
    throw new Error("createChatClient requires an injected `request` object with get/post/put methods");
  }
  const toErrorMessage = raw => typeof normalizeError === "function" ? normalizeError(raw) : typeof raw === "string" ? raw : (raw === null || raw === void 0 ? void 0 : raw.message) || "Request failed";
  async function call(method, url, payload) {
    if (!url) {
      throw new Error("createChatClient: missing endpoint for this call");
    }
    const response = method === "get" ? await request.get(url) : await request[method](url, payload);
    if (!(response !== null && response !== void 0 && response.status)) {
      throw new Error(toErrorMessage(response));
    }
    return response;
  }
  return {
    fetchChatList() {
      let queryString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
      return call("get", endpoints.chatList + queryString);
    },
    fetchMessages(chatPersonId) {
      let _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
        _ref2$searchMessage = _ref2.searchMessage,
        searchMessage = _ref2$searchMessage === void 0 ? "" : _ref2$searchMessage;
      let requestUrl = "".concat(endpoints.chatMessages, "?chatpersonid=").concat(chatPersonId);
      if (searchMessage) {
        requestUrl += "&searchmessage=".concat(encodeURIComponent(searchMessage));
      }
      return call("get", requestUrl);
    },
    sendMessage(payload) {
      return call("post", endpoints.sendChat, payload);
    },
    updateChat(chatId, payload) {
      return call("put", "".concat(endpoints.updateChat, "/").concat(chatId), payload);
    },
    deleteChat(chatId, payload) {
      return call("put", "".concat(endpoints.deleteChat, "/").concat(chatId), payload);
    },
    reactToChat(chatId, payload) {
      return call("put", "".concat(endpoints.reactChat, "/").concat(chatId), payload);
    },
    toggleStarChat(chatId, payload) {
      return call("put", "".concat(endpoints.starChat, "/").concat(chatId), payload);
    },
    removeRequireResponse(payload) {
      return call("put", endpoints.removeRequireResponse, payload);
    },
    updateTrackers(payload) {
      return call("put", endpoints.updateTrackers, payload);
    },
    translateChat(payload) {
      return call("post", endpoints.translateChat, payload);
    },
    fetchGroupListForChat() {
      let queryString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
      return call("get", endpoints.groupListForChat + queryString);
    },
    fetchTaskListForChat() {
      let queryString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
      return call("get", endpoints.taskListForChat + queryString);
    },
    async uploadFiles(files) {
      if (!request.uploadFiles) {
        throw new Error("createChatClient: `request.uploadFiles` was not supplied");
      }
      const tooBig = Array.from(files).some(file => file.size > _constants.MAX_UPLOAD_FILE_SIZE_BYTES);
      if (tooBig) {
        throw new Error("FILE_TOO_LARGE");
      }
      const response = await request.uploadFiles(endpoints.fileUpload, files);
      if (!(response !== null && response !== void 0 && response.status)) {
        throw new Error(toErrorMessage(response));
      }
      return response;
    },
    socketUrl
  };
}